import type { ApiSchemas } from '@rental/contracts/client'
import { accountOf } from '@rental/domain'
import { parseEuroCents } from '../money'

type Preview = ApiSchemas['CancellationPreview']
type Fee = ApiSchemas['CancellationFeeChoice']
/** A cancellation's body needs its reason; a no-show's may have one. */
export type EndingBody = ApiSchemas['CancelRequest'] | ApiSchemas['NoShowRequest']

/** What staff typed in the cancel or no-show dialog, as text. */
export interface EndingInput {
  /** With a policy: keep its fee, or (with prices.override) lower it. */
  choice: 'policy' | 'override'
  /** The fee typed without a policy, or the lowered one. */
  amount: string
  /** Why the fee was lowered. */
  feeReason: string
  /** Why it is cancelled (required), or a no-show's note (optional). */
  reason: string
}

export const EMPTY_ENDING: EndingInput = { choice: 'policy', amount: '', feeReason: '', reason: '' }

export type EndingProblem = 'amountInvalid' | 'overrideTooHigh' | 'reasonShort'

/** What the dialog still waits for before it can be sent. */
export type EndingWait = 'blocked' | 'fee' | 'lowerFee' | 'feeReason' | 'reason' | 'note'

export interface EndingDraft {
  /** What is kept, once the input says it plainly; null while it does not. */
  feeCents: number | null
  /** The balance afterwards: positive is still owed, negative goes back. */
  afterCents: number | null
  /** A field's problem, shown once it has text: an empty field is simply not ready. */
  problems: { amount?: EndingProblem; feeReason?: EndingProblem; reason?: EndingProblem }
  /** The request, once everything is ready; null while anything is missing. */
  body: EndingBody | null
  /** What it still waits for, in the dialog's order: empty exactly when there is a body. */
  waits: EndingWait[]
}

const REASON_MIN = 3

function filledButShort(text: string): boolean {
  const length = text.trim().length
  return length > 0 && length < REASON_MIN
}

/**
 * RES-5, as the dialog sees it. The server decides again on every request:
 * a policy's fee goes back as `expectedCents`, so a tier passing while the
 * dialog is open is refused rather than charged; a lower fee needs a reason;
 * a fee is typed only when the booking's terms say nothing (Q-27); nothing is
 * ever above the booking's total.
 */
export function endingDraft(preview: Preview, input: EndingInput): EndingDraft {
  const problems: EndingDraft['problems'] = {}
  const computed = preview.fee.computedCents
  const overriding =
    computed !== null && input.choice === 'override' && preview.canOverride && computed > 0

  let fee: Fee | null = null
  let feeCents: number | null = null
  if (computed === null) {
    const cents = parseEuroCents(input.amount)
    if (cents !== null && cents <= preview.totalCents) {
      feeCents = cents
      fee = { kind: 'typed', cents }
    } else if (input.amount.trim() !== '') problems.amount = 'amountInvalid'
  } else if (overriding) {
    const cents = parseEuroCents(input.amount)
    const lower = cents !== null && cents < computed
    if (lower) feeCents = cents
    else if (input.amount.trim() !== '') problems.amount = 'overrideTooHigh'
    if (filledButShort(input.feeReason)) problems.feeReason = 'reasonShort'
    if (lower && input.feeReason.trim().length >= REASON_MIN) {
      fee = {
        kind: 'override',
        expectedCents: computed,
        cents,
        reason: input.feeReason.trim(),
      }
    }
  } else {
    feeCents = computed
    fee = { kind: 'policy', expectedCents: computed }
  }

  const reason = input.reason.trim()
  if (filledButShort(input.reason)) problems.reason = 'reasonShort'
  const reasonReady =
    preview.kind === 'cancel' ? reason.length >= REASON_MIN : !filledButShort(reason)

  // The account's own formula (packages/domain), as the server's preview computes it.
  const afterCents =
    feeCents === null
      ? null
      : accountOf({
          status: preview.kind === 'cancel' ? 'cancelled' : 'no_show',
          totalCents: preview.totalCents,
          cancellationFeeCents: feeCents,
          chargesCents: preview.chargesCents,
          paidCents: preview.paidCents,
          refundedCents: preview.refundedCents,
          lostInDisputesCents: preview.lostInDisputesCents,
        }).balanceCents
  const body: EndingBody | null =
    !preview.allowed || !fee || !reasonReady
      ? null
      : preview.kind === 'cancel'
        ? { fee, reason }
        : { fee, ...(reason ? { reason } : {}) }
  const waits: EndingWait[] = []
  if (!preview.allowed) waits.push('blocked')
  if (!fee) {
    if (!overriding) waits.push('fee')
    else {
      if (feeCents === null) waits.push('lowerFee')
      if (input.feeReason.trim().length < REASON_MIN) waits.push('feeReason')
    }
  }
  if (!reasonReady) waits.push(preview.kind === 'cancel' ? 'reason' : 'note')
  return { feeCents, afterCents, problems, body, waits }
}
